/**
 * Controls reference (dashboard spec §4.2, card A5; D-617): the pause menu's Controls screen, generated from every
 * binding source and never hand-listed. `controlsRef.json` holds the groups, the row labels and the keycap names; each
 * row names binding ids (`bindings.keyboard.dock`, `reach.gamepad.cruise`, …) and the keys come from the data, so a
 * rebind (W2's Tab → dashboard, toolCycle → Backquote) changes the text with no edit here.
 *
 * Sources (`AllBindings`): `bindings.json` (keyboard, mouse, gamepad), `reach-controls.json`, `jettison.json` (its
 * keys, pad button and hold), the station screen's keys (`ui/nav.ts`: Q/E, LB/RB, L launch) and the map view's pad
 * axes and triggers (`maps/view/view.json`). The dashboard's keys join when W2 adds them.
 *
 * Drift: `allBindingActions` walks every source generically, so a new binding in any file shows up and the drift test
 * (`controlsRef.test.ts`) fails until a row covers it or the JSON lists it as tuning. Pure; config and UI code (not a
 * hot loop), so everything is immutable.
 */
import { z } from 'zod'
import { loadGameData } from '../data'
import jettisonJson from '../data/jettison.json'
import type { Bindings } from '../data/schemas'
import { loadMapsViewConfig, type MapsViewConfig } from '../maps/view/config'
import { loadReachControls, type ReachControls } from '../reach/controls'
import { keyToNavInput, UI_PAD, type NavInput } from '../ui/nav'
import refJson from './controlsRef.json'
import { PAD_NAV } from './menu'

// ---------------------------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------------------------

/** The jettison controls (`jettison.json`): tap the keys or pad button to pick the share, hold `holdS` to drop. */
export interface JettisonBindings {
  readonly keys: readonly string[]
  readonly padButton: number
  readonly holdS: number
}

/** The station screen's inputs: `KeyboardEvent.code` lists and standard-mapping pad buttons per `NavInput`. */
export interface StationBindings {
  readonly keyboard: Readonly<Record<NavInput, readonly string[]>>
  readonly gamepad: Readonly<Record<NavInput, number>>
}

/** Every binding source the reference is generated from. */
export interface AllBindings {
  readonly bindings: Bindings
  readonly reach: ReachControls
  readonly jettison: JettisonBindings
  readonly station: StationBindings
  /** The map view's pad cursor and orbit sticks and zoom triggers. */
  readonly map: MapsViewConfig['pad']
}

export interface ControlsRow {
  /** What the control does. */
  readonly action: string
  /** Keyboard and mouse column ("—" when unbound). */
  readonly keyboard: string
  /** Gamepad column; holds and chords are spelled out ("View (hold 0.5 s)", "L3 + R3"). */
  readonly pad: string
  /** The binding ids this row documents (the drift test's coverage). */
  readonly covers: readonly string[]
}

export interface ControlsGroup {
  /** flight · combat · travel · tools · fleet · comms · menus · station · map */
  readonly id: string
  readonly title: string
  readonly rows: readonly ControlsRow[]
}

// ---------------------------------------------------------------------------------------------
// controlsRef.json
// ---------------------------------------------------------------------------------------------

const text = z.string().min(1)
const partSchema = z
  .strictObject({
    ref: text.optional(),
    stick: z.tuple([text, text]).optional(),
    text: text.optional(),
    tap: z.literal(true).optional(),
    hold: z.union([z.literal(true), text]).optional(),
    plus: z.literal(true).optional(),
    then: z.literal(true).optional(),
  })
  .refine((p) => p.ref !== undefined || p.stick !== undefined || p.text !== undefined, 'a part needs ref, stick or text')

const refSchema = z.strictObject({
  comment: z.string().optional(),
  labels: z.strictObject({ join: text, plus: text, then: text, tap: text, hold: text, holdS: text, none: text, unknown: text }),
  keyNames: z.record(z.string(), text),
  padButtons: z.array(text),
  padAxes: z.array(text),
  sticks: z.tuple([text, text]),
  mouseButtons: z.array(text),
  padButtonFallback: text,
  padAxisFallback: text,
  tuning: z.array(text),
  leafObjects: z.array(text),
  groups: z
    .array(
      z.strictObject({
        id: text,
        title: text,
        rows: z
          .array(
            z.strictObject({ action: text, when: z.enum(['playerBatteries']).optional(), kb: z.array(partSchema).min(1), pad: z.array(partSchema).min(1) }),
          )
          .min(1),
      }),
    )
    .min(1),
})

type RefConfig = z.infer<typeof refSchema>
type Part = z.infer<typeof partSchema>

const REF: RefConfig = refSchema.parse(refJson)

// ---------------------------------------------------------------------------------------------
// Sources
// ---------------------------------------------------------------------------------------------


/** Every `KeyboardEvent.code` the station key map is probed with (arrows first, so they lead their row). */
const PROBE_CODES: readonly string[] = [
  'ArrowUp', 'ArrowDown', 'ArrowLeft', 'ArrowRight', 'Enter', 'NumpadEnter', 'Space', 'Backspace', 'Escape', 'Tab', 'Backquote',
  ...Array.from({ length: 26 }, (_, i) => `Key${String.fromCharCode(65 + i)}`),
  ...Array.from({ length: 10 }, (_, i) => `Digit${i}`),
  ...Array.from({ length: 12 }, (_, i) => `F${i + 1}`),
]

/** The station screen's keys and pad buttons, read from `ui/nav.ts` (`keyToNavInput`, `PAD_NAV`, `UI_PAD`). */
export function stationBindings(): StationBindings {
  const keys = (input: NavInput): readonly string[] => PROBE_CODES.filter((code) => keyToNavInput(code, false) === input)
  const keyboard: Record<NavInput, readonly string[]> = {
    up: keys('up'),
    down: keys('down'),
    left: keys('left'),
    right: keys('right'),
    activate: keys('activate'),
    back: keys('back'),
    prevTab: keys('prevTab'),
    nextTab: keys('nextTab'),
    launch: keys('launch'),
  }
  const gamepad: Record<NavInput, number> = {
    up: PAD_NAV.up,
    down: PAD_NAV.down,
    left: PAD_NAV.left,
    right: PAD_NAV.right,
    activate: PAD_NAV.a,
    back: PAD_NAV.b,
    prevTab: UI_PAD.lb,
    nextTab: UI_PAD.rb,
    launch: UI_PAD.y,
  }
  return { keyboard, gamepad }
}

const jettisonPick = z.object({ keys: z.array(text).min(1), padButton: z.number().int().nonnegative(), holdS: z.number().positive() })

/** Every binding source from the shipped data files; `bindings` defaults to the game data's (Zod-validated). */
export function loadAllBindings(bindings: Bindings = loadGameData().bindings): AllBindings {
  return {
    bindings,
    reach: loadReachControls(),
    jettison: jettisonPick.parse(jettisonJson),
    station: stationBindings(),
    map: loadMapsViewConfig().pad,
  }
}

// ---------------------------------------------------------------------------------------------
// The generic walk
// ---------------------------------------------------------------------------------------------

function isRecord(v: unknown): v is Readonly<Record<string, unknown>> {
  return typeof v === 'object' && v !== null && !Array.isArray(v)
}

/** A walked binding is one action: a key list, a number, a chord or an object naming its pad button(s). */
function isLeaf(id: string, v: unknown): boolean {
  if (!isRecord(v)) return true
  return typeof v.button === 'number' || Array.isArray(v.buttons) || REF.leafObjects.includes(id)
}

/** Every leaf binding id → its value (actions and tuning alike), in source order. `comment` fields are skipped. */
function walk(b: AllBindings): ReadonlyMap<string, unknown> {
  const out = new Map<string, unknown>()
  const visit = (id: string, v: unknown): void => {
    if (isLeaf(id, v)) {
      out.set(id, v)
      return
    }
    if (!isRecord(v)) return
    for (const [key, child] of Object.entries(v)) {
      if (key !== 'comment') visit(`${id}.${key}`, child)
    }
  }
  for (const [key, source] of Object.entries(b)) visit(key, source)
  return out
}

function isTuning(id: string): boolean {
  return REF.tuning.some((t) => id === t || id.startsWith(`${t}.`))
}

/** Every action id in every binding source (`source.path.action`), tuning values excluded. */
export function allBindingActions(b: AllBindings): string[] {
  return [...walk(b).keys()].filter((id) => !isTuning(id))
}

// ---------------------------------------------------------------------------------------------
// Labels
// ---------------------------------------------------------------------------------------------

function fill(template: string, k: string, s = ''): string {
  return template.replace('{k}', k).replace('{s}', s).replace('{n}', k)
}

function keyLabel(code: string): string {
  const named = REF.keyNames[code]
  if (named !== undefined) return named
  if (/^Key[A-Z]$/.test(code)) return code.slice(3)
  if (/^Digit\d$/.test(code)) return code.slice(5)
  if (/^Numpad\d$/.test(code)) return `Num ${code.slice(6)}`
  return code
}

function unique(labels: readonly string[]): string[] {
  return labels.filter((l, i) => labels.indexOf(l) === i)
}

function padButtonLabel(n: number): string {
  return REF.padButtons[n] ?? fill(REF.padButtonFallback, String(n))
}

function padAxisLabel(n: number): string {
  return REF.padAxes[n] ?? fill(REF.padAxisFallback, String(n))
}

/** One stick's name when `a` and `b` are its two axes (0/1 left, 2/3 right), else both axis names. */
function stickLabel(a: number, b: number): string {
  const lo = Math.min(a, b)
  if (Math.max(a, b) === lo + 1 && lo % 2 === 0 && lo / 2 < REF.sticks.length) return REF.sticks[lo / 2]
  return `${padAxisLabel(a)}${REF.labels.join}${padAxisLabel(b)}`
}

function isAxisId(id: string): boolean {
  return id.includes('.axes.') || /Axis$/.test(id) || /Axes$/.test(id)
}

/** A binding's keycap text from its value; `null` when the value can't be shown. */
function valueLabel(id: string, v: unknown): string | null {
  if (Array.isArray(v)) {
    if (v.every((x): x is string => typeof x === 'string')) return unique(v.map(keyLabel)).join(REF.labels.join)
    if (v.every((x): x is number => typeof x === 'number')) {
      if (isAxisId(id) && v.length === 2) return stickLabel(v[0], v[1])
      return v.map(padButtonLabel).join(REF.labels.plus)
    }
    return null
  }
  if (typeof v === 'number') {
    if (id.startsWith('bindings.mouse.')) return REF.mouseButtons[v] ?? null
    return isAxisId(id) ? padAxisLabel(v) : padButtonLabel(v)
  }
  if (isRecord(v)) {
    const buttons = v.buttons
    if (Array.isArray(buttons)) return valueLabel(id, buttons)
    const button = v.button
    if (typeof button !== 'number') return null
    const holdS = v.holdS
    const base = padButtonLabel(button)
    return typeof holdS === 'number' ? fill(REF.labels.holdS, base, String(holdS)) : base
  }
  return null
}

interface Cell {
  readonly text: string
  readonly covers: readonly string[]
}

function partLabel(p: Part, values: ReadonlyMap<string, unknown>, covers: string[]): string {
  let base: string
  if (p.stick !== undefined) {
    const [a, b] = p.stick
    covers.push(a, b)
    const va = values.get(a)
    const vb = values.get(b)
    base = typeof va === 'number' && typeof vb === 'number' ? stickLabel(va, vb) : REF.labels.unknown
  } else if (p.ref !== undefined) {
    covers.push(p.ref)
    base = p.text ?? valueLabel(p.ref, values.get(p.ref)) ?? REF.labels.unknown
  } else {
    base = p.text ?? REF.labels.unknown
  }
  if (p.tap === true) return fill(REF.labels.tap, base)
  if (p.hold === true) return fill(REF.labels.hold, base)
  if (p.hold !== undefined) {
    covers.push(p.hold)
    const s = values.get(p.hold)
    return typeof s === 'number' ? fill(REF.labels.holdS, base, String(s)) : REF.labels.unknown
  }
  return base
}

function cell(parts: readonly Part[], values: ReadonlyMap<string, unknown>): Cell {
  const covers: string[] = []
  let out = ''
  const seen: string[] = []
  for (const p of parts) {
    const label = partLabel(p, values, covers)
    if (p.plus === true) out += `${REF.labels.plus}${label}`
    else if (p.then === true) out += `${REF.labels.then}${label}`
    else if (!seen.includes(label)) out += out.length === 0 ? label : `${REF.labels.join}${label}`
    seen.push(label)
  }
  return { text: out.length === 0 ? REF.labels.none : out, covers }
}

// ---------------------------------------------------------------------------------------------
// The reference
// ---------------------------------------------------------------------------------------------

/**
 * Keys that gate rows (a row with `"when": "<key>"` shows only while the key is on). M10 P5a7b, review M2: the
 * batteries hold-fire row stays hidden while `weapons.json batteries.playerBatteries` is off, so the shipped reference
 * never lists a key that does nothing.
 */
export interface ControlsKeys {
  readonly playerBatteries: boolean
}

/** Every gated row shown (the drift and JSON-health checks cover every row, whatever the keys). */
export const ALL_CONTROLS_KEYS: ControlsKeys = { playerBatteries: true }

/** The keys as the shipped data sets them. */
export function shippedControlsKeys(data: { readonly weapons: { readonly batteries?: { readonly playerBatteries: boolean } } } = loadGameData()): ControlsKeys {
  return { playerBatteries: data.weapons.batteries?.playerBatteries === true }
}

/** The grouped controls reference (spec §4.2): Flight · Combat · Travel & navigation · … · Map; gated rows per `keys`. */
export function controlsReference(b: AllBindings, keys: ControlsKeys = shippedControlsKeys()): ControlsGroup[] {
  const values = walk(b)
  return REF.groups.map((g) => ({
    id: g.id,
    title: g.title,
    rows: g.rows.filter((r) => r.when === undefined || keys[r.when]).map((r) => {
      const kb = cell(r.kb, values)
      const pad = cell(r.pad, values)
      return { action: r.action, keyboard: kb.text, pad: pad.text, covers: unique([...kb.covers, ...pad.covers]) }
    }),
  }))
}

/** JSON health: row refs that name no binding, and tuning ids no source has (both must be empty). */
export function checkControlsRef(b: AllBindings): { unknownRefs: string[]; staleTuning: string[] } {
  const values = walk(b)
  const ids = [...values.keys()]
  const refs = unique(controlsReference(b, ALL_CONTROLS_KEYS).flatMap((g) => g.rows.flatMap((r) => [...r.covers])))
  return {
    unknownRefs: refs.filter((id) => !values.has(id)),
    staleTuning: REF.tuning.filter((t) => !ids.some((id) => id === t || id.startsWith(`${t}.`))),
  }
}
